import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { GoalTreeData } from '@/components/goal-tree'
import { type CalendarTask, InboxCalendar } from '@/components/inbox-calendar'
import { InboxTaskDetailSheet, type InboxTaskForSheet } from '@/components/inbox-task-detail-sheet'
import type { NodeDetail } from '@/components/node-detail-sheet'
import { NodeDetailSheet } from '@/components/node-detail-sheet'
import { optimisticDuration } from '@/lib/nodes-optimistic'
import { playDoneSound } from '@/lib/sounds'
import { useCalendarAllGoals } from '@/lib/use-calendar-all-goals'

type GoalTree = NonNullable<FunctionReturnType<typeof api.nodes.getGoalTree>>
type TaskNode = GoalTree['milestones'][number]['targets'][number]['tasks'][number]

// Calendar view over a single goal's tasks — reuses the inbox calendar grid,
// feeding it the goal's flattened leaf tasks and driving completion straight on
// the getGoalTree query so the whole detail screen stays in sync.
export default function GoalCalendar({
  tree,
  coverMode = false,
}: {
  tree: GoalTreeData
  coverMode?: boolean
}) {
  const goalId = tree._id
  const [detailNode, setDetailNode] = useState<NodeDetail | null>(null)
  const [detailGoalId, setDetailGoalId] = useState<Id<'nodes'>>(goalId)
  // Everyday tasks and habits from other goals open in their own sheet, not the goal task sheet.
  const [everydayItem, setEverydayItem] = useState<InboxTaskForSheet | null>(null)
  const {
    allGoals,
    setAllGoals,
    items: otherItems,
  } = useCalendarAllGoals('goal', {
    excludeGoalId: goalId,
  })

  const recurDone = useQuery(api.nodes.getRecurringCompletionDates, allGoals ? {} : 'skip')
  const completionDates = useMemo(
    () => new Map((recurDone ?? []).map((r) => [r.taskId as string, new Set(r.dates)])),
    [recurDone],
  )

  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((localStore, { taskId }) =>
    patchTaskInTree(localStore, goalId, taskId, { completedAt: Date.now(), status: 'done' }),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate(
    (localStore, { taskId }) =>
      patchTaskInTree(localStore, goalId, taskId, { completedAt: null, status: 'backlog' }),
  )
  const updateDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    optimisticDuration,
  )
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    (localStore, { taskId, dueDate }) =>
      patchTaskInTree(localStore, goalId, taskId, { dueDate: dueDate ?? undefined }),
  )
  const updateRecurs = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    (localStore, { taskId, recurrenceTime, recurrenceDay }) =>
      patchTaskInTree(localStore, goalId, taskId, { recurrenceTime, recurrenceDay }),
  )
  const setAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(
    (localStore, { taskId, allDay }) =>
      patchTaskInTree(
        localStore,
        goalId,
        taskId,
        allDay
          ? { allDay: true, recurrenceTime: undefined, durationMinutes: undefined }
          : { allDay: undefined },
      ),
  )

  const ownTasks: CalendarTask[] = tree.milestones.flatMap((ms) =>
    ms.targets.flatMap((tg) =>
      tg.tasks.map((task) => ({
        _id: task._id,
        title: task.title,
        completedAt: task.completedAt,
        effort: task.effort,
        recurs: task.recurs,
        recursPeriod: task.recursPeriod as CalendarTask['recursPeriod'],
        recursDays: task.recursDays,
        recurrenceDay: task.recurrenceDay,
        recurrenceTime: task.recurrenceTime,
        dueDate: task.dueDate ?? undefined,
        durationMinutes: task.durationMinutes,
        allDay: task.allDay,
        status: task.status,
        parentId: tg._id,
      })),
    ),
  )

  const tasks = allGoals && !tree.viewerIsCoach ? [...ownTasks, ...otherItems] : ownTasks
  const unscheduled = ownTasks.filter(
    (t) => !t.dueDate && !t.recursPeriod && !t.recurs && !t.completedAt,
  )

  return (
    <>
      <InboxCalendar
        tasks={tasks}
        completionDates={completionDates}
        coverMode={coverMode}
        allGoals={tree.viewerIsCoach ? undefined : { on: allGoals, onChange: setAllGoals }}
        unscheduled={unscheduled}
        onSchedule={(id, day, minutesOfDay) => {
          // minutesOfDay set → time came from a vertical block drag. Otherwise
          // preserve the task's existing time (moving days), or default to 9am.
          const existing = tasks.find((t) => t._id === id)?.dueDate
          const base = existing ? new Date(existing) : null
          const mins = minutesOfDay ?? (base ? base.getHours() * 60 + base.getMinutes() : 9 * 60)
          const due = new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate(),
            Math.floor(mins / 60),
            mins % 60,
          )
          updateDueDate({ taskId: id, dueDate: due.getTime() }).catch(() =>
            toast.error("Couldn't schedule task — please try again."),
          )
        }}
        onUnschedule={(id) =>
          updateDueDate({ taskId: id, dueDate: undefined }).catch(() =>
            toast.error("Couldn't unschedule task — please try again."),
          )
        }
        onRescheduleHabit={(id, day, minutes) => {
          const task = tasks.find((t) => t._id === id)
          const period = task?.recursPeriod ?? (task?.recurs ? 'daily' : undefined)
          if (!period) return
          const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
          // Weekly habits recur on a weekday; monthly on a day-of-month.
          const recurrenceDay =
            period === 'weekly'
              ? day.getDay()
              : period === 'monthly'
                ? day.getDate()
                : task?.recurrenceDay
          updateRecurs({
            taskId: id,
            recursPeriod: period,
            recurrenceTime: time,
            recurrenceDay,
          }).catch(() => toast.error("Couldn't reschedule habit — please try again."))
        }}
        onComplete={(id) => {
          playDoneSound()
          complete({ taskId: id }).catch(() =>
            toast.error("Couldn't update task — please try again."),
          )
        }}
        onUncomplete={(id) =>
          uncomplete({ taskId: id }).catch(() =>
            toast.error("Couldn't update task — please try again."),
          )
        }
        onDetail={(task) => {
          if (task.goalId && (task.isInbox || task.isHabit)) {
            setEverydayItem({
              _id: task._id,
              title: task.title,
              description: task.description,
              effort: task.effort,
              completedAt: task.completedAt,
              recursPeriod: task.recursPeriod,
              recursDays: task.recursDays,
              recurs: task.recurs,
              recurrenceTime: task.recurrenceTime,
              recurrenceDay: task.recurrenceDay,
              dueDate: task.dueDate,
              durationMinutes: task.durationMinutes,
              allDay: task.allDay,
              status: task.status,
              parentId: task.goalId,
              isHabit: task.isHabit,
              category: task.goalCategory,
            })
            return
          }
          setDetailGoalId(task.goalId ?? goalId)
          setDetailNode({
            nodeId: task._id,
            type: 'task',
            title: task.title,
            description: task.goalId
              ? task.description
              : tree.milestones
                  .flatMap((ms) => ms.targets.flatMap((tg) => tg.tasks))
                  .find((x) => x._id === task._id)?.description,
            category: task.goalCategory ?? tree.category,
            goalTitle: task.goalTitle ?? tree.title,
            effort: task.effort,
            completedAt: task.completedAt,
            status: task.status,
            dueDate: task.dueDate,
            recurs: task.recurs,
            recursPeriod: task.recursPeriod,
            recursDays: task.recursDays,
            recurrenceDay: task.recurrenceDay,
            recurrenceTime: task.recurrenceTime,
            durationMinutes: task.durationMinutes,
            allDay: task.allDay,
          })
        }}
        onDurationChange={(id, minutes) => updateDuration({ taskId: id, durationMinutes: minutes })}
        onSetAllDay={(id, day) => {
          const task = tasks.find((t) => t._id === id)
          if (!(task?.recursPeriod || task?.recurs)) {
            const due = new Date(day.getFullYear(), day.getMonth(), day.getDate())
            updateDueDate({ taskId: id, dueDate: due.getTime() }).catch(() =>
              toast.error("Couldn't schedule task — please try again."),
            )
          }
          setAllDay({ taskId: id, allDay: true }).catch(() =>
            toast.error("Couldn't update task — please try again."),
          )
        }}
      />
      <NodeDetailSheet
        node={detailNode}
        goalId={detailGoalId}
        viewerIsCoach={tree.viewerIsCoach}
        onClose={() => setDetailNode(null)}
      />
      <InboxTaskDetailSheet
        task={everydayItem}
        onClose={() => setEverydayItem(null)}
        onComplete={(id) => {
          playDoneSound()
          complete({ taskId: id }).catch(() =>
            toast.error("Couldn't update task — please try again."),
          )
        }}
        onUncomplete={(id) =>
          uncomplete({ taskId: id }).catch(() =>
            toast.error("Couldn't update task — please try again."),
          )
        }
      />
    </>
  )
}

function patchTaskInTree(
  localStore: OptimisticLocalStore,
  goalId: Id<'nodes'>,
  taskId: Id<'nodes'>,
  patch: Partial<TaskNode>,
) {
  const tree = localStore.getQuery(api.nodes.getGoalTree, { goalId })
  if (!tree) return
  localStore.setQuery(
    api.nodes.getGoalTree,
    { goalId },
    {
      ...tree,
      milestones: tree.milestones.map((ms) => ({
        ...ms,
        targets: ms.targets.map((tg) => ({
          ...tg,
          tasks: tg.tasks.map((task) => (task._id === taskId ? { ...task, ...patch } : task)),
        })),
      })),
    },
  )
}
